import { computed, ref, watchEffect } from 'vue'
import { createInjectionState } from '@vueuse/shared'
import { usePreferredLanguages } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { usePrimeVue } from 'primevue/config'
import { useAuthStore } from '@/modules/auth'
import api from '../axios'
import { locales as primeLocales } from '../theme/locales'
import {
  normalizeAppLocale,
  readStoredUserLocale,
  resolveDeviceLocale,
  writeStoredUserLocale,
  type AppLocale,
} from './resolveDeviceLocale'

const applyLocale = (
  next: AppLocale,
  i18nLocale: { value: string },
  primevue: ReturnType<typeof usePrimeVue>,
) => {
  i18nLocale.value = next
  api.defaults.headers.common['Accept-Language'] = next
  primevue.config.locale = primeLocales[next]
}

/**
 * Locale efectivo con prioridad:
 * 1. Preferencia personal (localStorage, desde Perfil en ModalSettings)
 * 2. Configuración del tenant (System settings)
 * 3. Idioma del dispositivo
 */
const [useProvideLocale, injectModule] = createInjectionState(() => {
  const preferredLanguages = usePreferredLanguages()
  const { locale: i18nLocale } = useI18n()
  const primevue = usePrimeVue()
  const auth = useAuthStore()

  const userLocale = ref<AppLocale | null>(readStoredUserLocale())

  const deviceLocale = computed<AppLocale>(() =>
    resolveDeviceLocale(preferredLanguages.value),
  )

  const tenantLocale = computed<AppLocale | null>(() =>
    normalizeAppLocale(auth.user?.tenant?.settings?.locale),
  )

  const locale = computed<AppLocale>(() => {
    if (userLocale.value) {
      return userLocale.value
    }

    if (tenantLocale.value) {
      return tenantLocale.value
    }

    return deviceLocale.value
  })

  watchEffect(() => {
    applyLocale(locale.value, i18nLocale, primevue)
  })

  const setUserLocale = (next: AppLocale | null) => {
    userLocale.value = next
    writeStoredUserLocale(next)
  }

  /** Aplica de inmediato un locale (p. ej. tras guardar ajustes del tenant). */
  const applyLocaleNow = (next: AppLocale) => {
    applyLocale(next, i18nLocale, primevue)
  }

  return {
    locale,
    deviceLocale,
    tenantLocale,
    userLocale,
    setUserLocale,
    applyLocaleNow,
  }
})

export { useProvideLocale }

export const useLocale = () => {
  const module = injectModule()

  if (module == null) {
    throw new Error(
      'Please call `useProvideLocale` on the appropriate parent component',
    )
  }

  return module
}
